import React, { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Collapse,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import LinkIcon from '@mui/icons-material/Link';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArticleIcon from '@mui/icons-material/Article';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EmailIcon from '@mui/icons-material/Email';
import AppsIcon from '@mui/icons-material/Apps';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import { useMenuLinkNavigation } from '../../../hooks/useMenuLinkNavigation';
import { MegaMenuDropdown } from './mega-menu-dropdown';
import { useMenusData } from '../../../hooks/useMenusData';
import { IMenuResponse } from '../../../model/base/response/menu-response';
import { useGlobalState } from '../../global/context/global-context';
import { getEntityLogoSrc, getEntityPowerLabel, getFormattedEntityName } from '../../../utils/entity-formatter';
import { useSystemLinks } from './system-links';
import { MAIN_MENU_ID } from './skip-links';

export interface SubMenuItem {
  label: string;
  path: string;
  icon?: React.ReactElement;
  isExternal?: boolean;
}

export interface MenuItem {
  label: string;
  path?: string;
  isExternal?: boolean;
  icon: React.ReactElement;
  subItems?: SubMenuItem[];
  isMegaMenu?: boolean;
}

// Estrutura padrão da barra principal, usada enquanto a entidade não tem os menus fixos cadastrados no APM
const defaultMenuStructure: MenuItem[] = [
  {
    label: 'Início',
    path: '/',
    icon: <HomeIcon />
  },

  {
    label: 'O Município',
    icon: <LocationCityIcon />,
    subItems: [
      { label: 'A Cidade', path: '/a-cidade', icon: <InfoIcon /> },
      { label: 'Estrutura Administrativa', path: '/estrutura-administrativa', icon: <BusinessIcon /> },
      { label: 'Projetos Governamentais', path: '/projetos-governamentais', icon: <AccountBalanceIcon /> },
    ],
  },
  {
    label: 'Notícias',
    path: '/noticias',
    icon: <NewspaperIcon />
  },
  {
    label: 'Diário Oficial',
    icon: <DescriptionIcon />,
    subItems: [
      { label: 'Edições', path: '/diario-oficial', icon: <DescriptionIcon /> },
      { label: 'Publicações', path: '/publicacoes', icon: <ArticleIcon /> },
    ],
  },

  {
    label: 'Serviços',
    icon: <FlashOnIcon />,
    subItems: [
      { label: 'Perguntas Frequentes', path: '/perguntas-respostas', icon: <QuestionAnswerIcon /> },
      { label: 'Links Úteis', path: '/links-uteis', icon: <LinkIcon /> },
    ],
  },
  {
    label: 'Transparência',
    icon: <VisibilityIcon />,
    subItems: [
      { label: 'Contas Públicas', path: '/contas-publicas', icon: <VisibilityIcon /> },
      { label: 'Licitações', path: '/licitacoes', icon: <GavelIcon /> },
      { label: 'Contratos e Aquisições', path: '/contratos-aquisicoes', icon: <ArticleIcon /> },
      { label: 'Convênios e Repasses', path: '/convenios-repasses', icon: <HandshakeIcon /> },
      { label: 'Auditoria', path: '/auditoria', icon: <AssessmentIcon /> },
    ],
  },
  {
    label: 'Fale Conosco',
    path: '/contato',
    icon: <EmailIcon />
  },
];

// "Mais opções" agrupa os menus criados pela entidade e fica sempre no fim da barra
const megaMenuEntry: MenuItem = {
  label: 'Mais opções',
  icon: <AppsIcon />,
  isMegaMenu: true
};

// Ícones dos menus e itens fixos do portal, pela chave (tblMenu/tblItemMenu.DesChave)
const fixedMenuIcons: Record<string, React.ReactElement> = {
  INICIO: <HomeIcon />,
  MUNICIPIO: <LocationCityIcon />,
  NOTICIAS: <NewspaperIcon />,
  DIARIO_OFICIAL: <DescriptionIcon />,
  SERVICOS: <FlashOnIcon />,
  TRANSPARENCIA: <VisibilityIcon />,
  FALE_CONOSCO: <EmailIcon />,
};

const fixedItemIcons: Record<string, React.ReactElement> = {
  A_CIDADE: <InfoIcon />,
  ESTRUTURA_ADMINISTRATIVA: <BusinessIcon />,
  PROJETOS_GOVERNAMENTAIS: <AccountBalanceIcon />,
  EDICOES: <DescriptionIcon />,
  PUBLICACOES: <ArticleIcon />,
  PERGUNTAS_FREQUENTES: <QuestionAnswerIcon />,
  LINKS_UTEIS: <LinkIcon />,
  CONTAS_PUBLICAS: <VisibilityIcon />,
  LICITACOES: <GavelIcon />,
  CONTRATOS_AQUISICOES: <ArticleIcon />,
  CONVENIOS_REPASSES: <HandshakeIcon />,
  AUDITORIA: <AssessmentIcon />,
};

function toMenuItem(menu: IMenuResponse): MenuItem | null {
  // Rótulo exibido como cadastrado no APM
  const label = menu.name || '';
  const icon = (menu.key && fixedMenuIcons[menu.key]) || <LinkIcon />;

  // Menus de link direto (rota, URL ou página customizada) não abrem dropdown; os de dropdown sem itens publicados não aparecem
  if (menu.url) return { label, path: menu.url, isExternal: menu.isExternal, icon };
  if (!menu.items?.length) return null;

  return {
    label,
    icon,
    subItems: menu.items
      .filter((item) => !!item.url)
      .map((item) => ({
        label: item.name || '',
        path: item.url!,
        isExternal: item.isExternal,
        icon: (item.key && fixedItemIcons[item.key]) || (item.isExternal ? <OpenInNewIcon /> : <LinkIcon />),
      })),
  };
}

/**
 * Estrutura da barra principal na ordem definida pela entidade (ou a padrão, sem "Mais opções").
 * Compartilhada com o mapa do site.
 */
export const useMainMenuStructure = (): MenuItem[] => {
  const { mainMenus } = useMenusData();
  return mainMenus.length > 0
    ? mainMenus.map(toMenuItem).filter((item): item is MenuItem => item !== null)
    : defaultMenuStructure;
};

/** Normaliza um destino de menu para comparação com a rota atual ("/noticias/" → "noticias"). */
const normalizePath = (path: string) => path.replace(/^\/+|\/+$/g, '').split(/[?#]/)[0];

/**
 * Rota atual sem o prefixo /:state/:city, normalizada; usada para destacar o menu ativo.
 */
const useCurrentPath = () => {
  const { pathname } = useLocation();
  const { state, city } = useParams<{ state?: string; city?: string }>();
  const prefix = state && city ? `/${state}/${city}` : '';
  const path = prefix && pathname.startsWith(prefix) ? pathname.substring(prefix.length) : pathname;
  return normalizePath(path);
};

const isPathActive = (currentPath: string, target?: string, isExternal?: boolean) => {
  if (!target || isExternal || /^https?:\/\//i.test(target) || target.startsWith('/#')) return false;
  const normalized = normalizePath(target);
  if (normalized === '') return currentPath === '';
  return currentPath === normalized || currentPath.startsWith(`${normalized}/`);
};

const isMenuActive = (currentPath: string, item: MenuItem) =>
  item.subItems
    ? item.subItems.some((sub) => isPathActive(currentPath, sub.path, sub.isExternal))
    : isPathActive(currentPath, item.path, item.isExternal);

const menuIdOf = (label: string) => `menu-${label.toLowerCase().normalize('NFD').replace(/[^a-z0-9]+/g, '-')}`;

// Botão da barra principal: indicador inferior de 3px no item ativo
const navButtonSx = (active: boolean) => ({
  position: 'relative',
  height: 56,
  minHeight: 56,
  px: 1.75,
  borderRadius: 0,
  color: active ? 'brand.text' : 'text.primary',
  fontSize: '0.9375rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  '& .MuiButton-endIcon': { ml: 0.25 },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 12,
    right: 12,
    bottom: 0,
    height: 3,
    borderRadius: '3px 3px 0 0',
    backgroundColor: 'primary.main',
    transform: active ? 'scaleX(1)' : 'scaleX(0)',
    transition: 'transform 200ms cubic-bezier(0.22, 1, 0.36, 1)',
  },
  '&:hover': {
    backgroundColor: 'brand.surface',
    color: 'brand.text',
  },
  '&:active': { transform: 'none' },
});

interface AppMenuProps {
  isMobile: boolean;
  open?: boolean;
  onClose: () => void;
}

export default function AppMenu({ isMobile, open = false, onClose }: AppMenuProps) {
  const openMenuLink = useMenuLinkNavigation();
  const { menus } = useMenusData();
  const mainStructure = useMainMenuStructure();
  const currentPath = useCurrentPath();
  const [anchorEl, setAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});
  const [mobileOpenMenus, setMobileOpenMenus] = useState<{ [key: string]: boolean }>({});
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [megaMenuAnchor, setMegaMenuAnchor] = useState<HTMLElement | null>(null);

  // Barra principal na ordem definida pela entidade (ou a padrão); "Mais opções" no fim, apenas se houver menus
  const hasMenus = menus && menus.length > 0;
  const filteredMenuStructure = hasMenus ? [...mainStructure, megaMenuEntry] : mainStructure;

  const handleNavigation = (path: string, isExternal?: boolean) => {
    openMenuLink(path, isExternal);
    onClose();
    handleCloseMenu();
  };

  const handleOpenMenu = (label: string, event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl({ ...anchorEl, [label]: event.currentTarget });
  };

  const handleCloseMenu = () => {
    setAnchorEl({});
  };

  const handleMobileToggle = (label: string) => {
    setMobileOpenMenus({
      ...mobileOpenMenus,
      [label]: !mobileOpenMenus[label],
    });
  };

  const handleMegaMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMegaMenuAnchor(event.currentTarget);
    setMegaMenuOpen(true);
  };

  const handleMegaMenuClose = () => {
    setMegaMenuOpen(false);
    // Devolve o foco ao botão que abriu o "Mais opções"
    megaMenuAnchor?.focus();
  };

  // Desktop Menu with Dropdowns
  if (!isMobile) {
    return (
      <>
        <Box
          component="ul"
          id={MAIN_MENU_ID}
          tabIndex={-1}
          aria-label="Menu principal"
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            listStyle: 'none',
            m: 0,
            p: 0,
            '&:focus': { outline: 'none' },
          }}
        >
          {filteredMenuStructure.map((item) => {
            const active = !item.isMegaMenu && isMenuActive(currentPath, item);
            const menuId = menuIdOf(item.label);

            return (
              <Box component="li" key={item.label} sx={{ display: 'flex' }}>
                {item.isMegaMenu ? (
                  // Mega Menu
                  <Button
                    onClick={handleMegaMenuOpen}
                    endIcon={<KeyboardArrowDownIcon />}
                    aria-haspopup="true"
                    aria-expanded={megaMenuOpen}
                    sx={navButtonSx(megaMenuOpen)}
                  >
                    {item.label}
                  </Button>
                ) : item.subItems ? (
                  // Menu with dropdown
                  <>
                    <Button
                      id={`${menuId}-button`}
                      onClick={(e) => handleOpenMenu(item.label, e)}
                      endIcon={<KeyboardArrowDownIcon />}
                      aria-haspopup="true"
                      aria-expanded={Boolean(anchorEl[item.label])}
                      aria-controls={anchorEl[item.label] ? menuId : undefined}
                      sx={navButtonSx(active)}
                    >
                      {item.label}
                    </Button>
                    <Menu
                      id={menuId}
                      anchorEl={anchorEl[item.label]}
                      open={Boolean(anchorEl[item.label])}
                      onClose={handleCloseMenu}
                      MenuListProps={{
                        'aria-labelledby': `${menuId}-button`,
                        sx: { py: 1 },
                      }}
                      PaperProps={{
                        sx: {
                          mt: 0.5,
                          minWidth: 260,
                        },
                      }}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                    >
                      {item.subItems.map((subItem) => {
                        const subActive = isPathActive(currentPath, subItem.path, subItem.isExternal);
                        return (
                          <MenuItem
                            key={`${subItem.label}-${subItem.path}`}
                            selected={subActive}
                            aria-current={subActive ? 'page' : undefined}
                            onClick={() => handleNavigation(subItem.path, subItem.isExternal)}
                            sx={{
                              gap: 1.5,
                              py: 1.25,
                              px: 2,
                              mx: 1,
                              borderRadius: 1,
                              fontSize: '0.9375rem',
                              fontWeight: subActive ? 600 : 400,
                              '&:hover, &.Mui-selected, &.Mui-selected:hover': {
                                backgroundColor: 'brand.surface',
                                color: 'brand.text',
                              },
                            }}
                          >
                            {subItem.icon && (
                              <Box
                                component="span"
                                aria-hidden
                                sx={{
                                  display: 'inline-flex',
                                  color: 'brand.text',
                                  '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                                }}
                              >
                                {subItem.icon}
                              </Box>
                            )}
                            <Box component="span" sx={{ flex: 1 }}>{subItem.label}</Box>
                            {subItem.isExternal && (
                              <OpenInNewIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} aria-label="abre em nova aba" />
                            )}
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </>
                ) : (
                  // Simple menu item
                  <Button
                    onClick={() => item.path && handleNavigation(item.path, item.isExternal)}
                    aria-current={active ? 'page' : undefined}
                    sx={navButtonSx(active)}
                  >
                    {item.label}
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Mega Menu Dropdown */}
        <MegaMenuDropdown
          open={megaMenuOpen}
          onClose={handleMegaMenuClose}
          anchorEl={megaMenuAnchor}
        />
      </>
    );
  }

  // Mobile Drawer with Expandable Lists
  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 320,
          maxWidth: '88vw',
          borderRadius: 0,
        },
      }}
    >
      <MobileDrawerHeader onClose={onClose} />
      <List component="nav" aria-label="Menu principal" sx={{ py: 1 }}>
        {filteredMenuStructure.map((item) => {
          const active = !item.isMegaMenu && isMenuActive(currentPath, item);
          const itemSx = {
            minHeight: 48,
            mx: 1,
            borderRadius: 1,
            ...(active && { backgroundColor: 'brand.surface', color: 'brand.text' }),
          };
          const label = (
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontSize: '1rem', fontWeight: 600 }}
            />
          );
          const icon = (
            <ListItemIcon sx={{ color: 'brand.text', minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
          );

          return (
            <React.Fragment key={item.label}>
              {item.isMegaMenu ? (
                // Mega Menu no mobile
                <ListItem disablePadding>
                  <ListItemButton onClick={handleMegaMenuOpen} sx={itemSx}>
                    {icon}
                    {label}
                  </ListItemButton>
                </ListItem>
              ) : item.subItems ? (
                // Menu with sub-items
                <>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleMobileToggle(item.label)}
                      aria-expanded={!!mobileOpenMenus[item.label]}
                      sx={itemSx}
                    >
                      {icon}
                      {label}
                      {mobileOpenMenus[item.label] ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={mobileOpenMenus[item.label]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.subItems.map((subItem) => {
                        const subActive = isPathActive(currentPath, subItem.path, subItem.isExternal);
                        return (
                          <ListItemButton
                            key={`${subItem.label}-${subItem.path}`}
                            aria-current={subActive ? 'page' : undefined}
                            sx={{
                              pl: 7,
                              minHeight: 44,
                              mx: 1,
                              borderRadius: 1,
                              ...(subActive && { color: 'brand.text', fontWeight: 600 }),
                            }}
                            onClick={() => handleNavigation(subItem.path, subItem.isExternal)}
                          >
                            <ListItemText
                              primary={subItem.label}
                              primaryTypographyProps={{
                                fontSize: '0.9375rem',
                                fontWeight: subActive ? 600 : 400,
                              }}
                            />
                            {subItem.isExternal && <OpenInNewIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                </>
              ) : (
                // Simple menu item
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => item.path && handleNavigation(item.path, item.isExternal)}
                    aria-current={active ? 'page' : undefined}
                    sx={itemSx}
                  >
                    {icon}
                    {label}
                  </ListItemButton>
                </ListItem>
              )}
            </React.Fragment>
          );
        })}
      </List>

      <MobileSystemLinks onNavigate={handleNavigation} />

      {/* Mega Menu Dropdown para Mobile */}
      <MegaMenuDropdown
        open={megaMenuOpen}
        onClose={handleMegaMenuClose}
        anchorEl={megaMenuAnchor}
      />
    </Drawer>
  );
}

function MobileDrawerHeader({ onClose }: { onClose: () => void }) {
  const { applicationData } = useGlobalState();
  const entity = applicationData?.entity;
  const logo = getEntityLogoSrc(entity);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {logo && (
        <Box component="img" src={logo} alt="" sx={{ height: 40, width: 'auto', objectFit: 'contain' }} />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" component="p" sx={{ fontFamily: (theme) => theme.typography.h1.fontFamily, lineHeight: 1.25 }}>
          {getFormattedEntityName(entity)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {getEntityPowerLabel(entity)}
        </Typography>
      </Box>
      <IconButton onClick={onClose} aria-label="fechar menu" sx={{ width: 44, height: 44 }}>
        <CloseIcon />
      </IconButton>
    </Box>
  );
}

// Ouvidoria, sistemas e acessibilidade, que no mobile não cabem na barra utilitária
function MobileSystemLinks({ onNavigate }: { onNavigate: (path: string, isExternal?: boolean) => void }) {
  const systemLinks = useSystemLinks();

  return (
    <>
      <Divider sx={{ mx: 2 }} />
      <List
        dense
        subheader={
          <ListSubheader component="div" disableSticky sx={{ typography: 'overline', color: 'text.secondary', lineHeight: 3 }}>
            Serviços e sistemas
          </ListSubheader>
        }
        sx={{ pb: 2 }}
      >
        {systemLinks.map((link) => (
          <ListItemButton
            key={link.label}
            component="a"
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ minHeight: 44, mx: 1, borderRadius: 1 }}
          >
            <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>{link.icon}</ListItemIcon>
            <ListItemText
              primary={link.description ? `${link.label} — ${link.description}` : link.label}
              primaryTypographyProps={{ fontSize: '0.9375rem' }}
            />
            <OpenInNewIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          </ListItemButton>
        ))}
        <ListItemButton onClick={() => onNavigate('/acessibilidade')} sx={{ minHeight: 44, mx: 1, borderRadius: 1 }}>
          <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}><AccessibilityNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Acessibilidade" primaryTypographyProps={{ fontSize: '0.9375rem' }} />
        </ListItemButton>
      </List>
    </>
  );
}
